import React, { useState } from "react";
import { api, exportCSV, money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { printInvoice } from "@/lib/invoice";
import {
  ChartLineUp as ChartLineUpIcon, Users as UsersIcon, Package as PackageIcon,
  Truck as TruckIcon, CurrencyDollar as CurrencyDollarIcon, Printer as PrinterIcon, FileXls as FileXlsIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  { key: "sales", label: "Sales Report", icon: CurrencyDollarIcon, desc: "All transactions with totals & GST" },
  { key: "customers", label: "Customer Report", icon: UsersIcon, desc: "Accounts, credit limits & balances" },
  { key: "sales_staff", label: "Sales by Staff (Weekly)", icon: UsersIcon, desc: "Top cashiers/drivers this week" },
  { key: "inventory", label: "Inventory Report", icon: PackageIcon, desc: "Stock levels & reorder points" },
  { key: "fleet", label: "Fleet Report", icon: TruckIcon, desc: "Vehicles, service & maintenance" },
  { key: "financial", label: "Financial Ledger", icon: ChartLineUpIcon, desc: "General ledger entries" },
];

const COLS = {
  sales: [["invoice_no", "Invoice"], ["customer_name", "Customer"], ["payment_type", "Payment"], ["total", "Total", true], ["created_at", "Date", false, true]],
  customers: [["name", "Name"], ["type", "Type"], ["town", "Town"], ["credit_limit", "Limit", true], ["balance", "Balance", true]],
  sales_staff: [["staff", "Staff"], ["sales_count", "Sales (7d)"], ["total", "Total (7d)", true]],
  inventory: [["name", "Item"], ["category", "Category"], ["quantity", "Qty"], ["price", "Price", true]],
  fleet: [["name", "Vehicle"], ["type", "Type"], ["plate", "Plate"], ["status", "Status"], ["mileage", "Mileage"]],
  financial: [["date", "Date", false, true], ["account", "Account"], ["description", "Description"], ["debit", "Debit", true], ["credit", "Credit", true]],
};

export default function Reports() {
  const [active, setActive] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async (key) => {
    setActive(key); setLoading(true);
    try { const { data } = await api.get(`/reports/${key}`); setData(data); }
    catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const cols = active ? COLS[active] : [];

  return (
    <div className="space-y-5 animate-fade-up">
      <div><h1 className="font-head text-2xl font-bold">Reports</h1><p className="text-muted-foreground text-sm">Generate, export to Excel/CSV & print operational reports.</p></div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <button key={r.key} data-testid={`report-${r.key}`} onClick={() => run(r.key)}
              className={`border rounded-lg bg-card p-4 text-left hover:-translate-y-0.5 transition-transform ${active === r.key ? "border-primary ring-1 ring-primary" : ""}`}>
              <span className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Icon size={20} weight="duotone" /></span>
              <div className="mt-3 font-medium text-sm">{r.label}</div>
              <div className="text-[11px] text-muted-foreground">{r.desc}</div>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="border rounded-lg bg-card">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="font-head font-semibold">{REPORTS.find((r) => r.key === active)?.label} <span className="text-muted-foreground font-normal text-sm">({data.length} records)</span></div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => window.print()}><PrinterIcon size={16} className="mr-1.5" />Print / PDF</Button>
              <Button variant="outline" size="sm" data-testid="report-export-csv" onClick={() => exportCSV(`${active}_report.csv`, data)}><FileXlsIcon size={16} className="mr-1.5" />Export Excel/CSV</Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50">{cols.map((c) => <TableHead key={c[0]} className={c[2] ? "text-right" : ""}>{c[1]}</TableHead>)}{active === "sales" && <TableHead className="text-right">Invoice</TableHead>}</TableRow></TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={cols.length + (active === "sales" ? 1 : 0)} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>}
                {!loading && data.map((row, i) => (
                  <TableRow key={i}>
                    {cols.map((c) => (
                      <TableCell key={c[0]} className={`py-2 ${c[2] ? "text-right" : ""}`}>
                        {c[3] ? fmtDate(row[c[0]]) : c[2] ? money(row[c[0]]) : (row[c[0]] ?? "-")}
                      </TableCell>
                    ))}
                    {active === "sales" && (
                      <TableCell className="py-2 text-right">
                        <button data-testid="report-print-invoice" onClick={() => printInvoice(row)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Print invoice">
                          <PrinterIcon size={16} />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
